import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

const fmt = (val, decimals = 2) =>
    `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

const fmtNum = (val) => Number(val || 0).toLocaleString('en-IN');

const StatCard = ({ icon, label, value, sub, color = '#4d8af0', bg = '#eef3ff' }) => (
    <div style={{
        background: bg, borderRadius: 10, padding: '14px 16px',
        borderLeft: `3px solid ${color}`, display: 'flex', flexDirection: 'column', gap: 4,
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                {label}
            </span>
            <i className={`bi ${icon}`} style={{ fontSize: 16, color, opacity: 0.8 }} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
);

const SectionHeader = ({ title, icon }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, marginTop: 4 }}>
        <i className={`bi ${icon}`} style={{ fontSize: 15, color: '#4d8af0' }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            {title}
        </span>
        <div style={{ flex: 1, height: 1, background: '#e5e7eb', marginLeft: 4 }} />
    </div>
);

const RankRow = ({ rank, name, qty, isTop }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '7px 10px', borderRadius: 7,
        background: rank === 1 ? (isTop ? '#f0fdf4' : '#fff5f5') : '#f9fafb',
        marginBottom: 4,
    }}>
        <div style={{
            width: 22, height: 22, borderRadius: '50%', fontSize: 11, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: rank === 1 ? (isTop ? '#22c55e' : '#ef4444') : '#e5e7eb',
            color: rank === 1 ? '#fff' : '#6b7280',
        }}>
            {rank}
        </div>
        <span style={{ flex: 1, fontSize: 12, color: '#374151', fontWeight: 500 }}>
            {name === 'N/A' ? '—' : name}
        </span>
        <span style={{
            fontSize: 12, fontWeight: 700,
            color: isTop ? '#16a34a' : '#dc2626',
            background: isTop ? '#dcfce7' : '#fee2e2',
            padding: '2px 8px', borderRadius: 99,
        }}>
            {qty} sold
        </span>
    </div>
);

const HourlyBar = ({ slot, orders, sales, maxOrders }) => {
    const pct = maxOrders > 0 ? (orders / maxOrders) * 100 : 0;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ width: 120, fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>{slot}</span>
            <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: '#4d8af0', borderRadius: 99, transition: 'width 0.5s' }} />
            </div>
            <span style={{ width: 55, fontSize: 11, color: '#374151', fontWeight: 600, textAlign: 'right' }}>
                {orders} orders
            </span>
            <span style={{ width: 80, fontSize: 11, color: '#059669', fontWeight: 600, textAlign: 'right' }}>
                {fmt(sales)}
            </span>
        </div>
    );
};

const CouponRow = ({ coupon, used, discount }) => (
    <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '7px 10px', borderRadius: 7, background: '#f9fafb', marginBottom: 4,
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-tag-fill" style={{ fontSize: 12, color: '#8b5cf6' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                {coupon}
            </span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}>{used}× used</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>−{fmt(discount)}</span>
        </div>
    </div>
);

const OutletDetailsModal = ({ isOpen, onClose, outletName, data, loading }) => {
    const { stats, hourlyDistribution, topProducts, bottomProducts, topCoupons,
        totalOnlineSales, outletShiftSummary, pettyCashSummary,
        damagedProductsSummary, exhaustedStockSummary, userStatsSummary } = data || {};

    const maxOrders = useMemo(() =>
        Math.max(...(hourlyDistribution || []).map(h => h.totalOrders || 0), 1),
        [hourlyDistribution]);

    const formatShiftTime = (isoStr) => {
        if (!isoStr) return '—';
        return new Date(isoStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1055,
                background: 'rgba(15,23,42,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px',
                backdropFilter: 'blur(2px)',
            }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div style={{
                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 1000,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
                overflow: 'hidden',
            }}>
                {/* Modal Header */}
                <div style={{
                    padding: '18px 24px', borderBottom: '1px solid #e5e7eb',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: 'linear-gradient(135deg, #f8faff 0%, #eef3ff 100%)',
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 40, height: 40, borderRadius: 10, background: '#4d8af0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className="bi bi-shop" style={{ fontSize: 18, color: '#fff' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                                {outletName || 'Outlet Details'}
                            </div>
                            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>
                                Today's Performance Summary
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: 'none', background: '#f3f4f6', borderRadius: 8,
                            width: 32, height: 32, cursor: 'pointer', fontSize: 16,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#6b7280', transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#e5e7eb'}
                        onMouseLeave={e => e.currentTarget.style.background = '#f3f4f6'}
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                {/* Modal Body */}
                <div style={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280, flexDirection: 'column', gap: 12 }}>
                            <div className="spinner-border text-primary" role="status" style={{ width: 36, height: 36 }} />
                            <span style={{ fontSize: 13, color: '#6b7280' }}>Loading outlet data…</span>
                        </div>
                    ) : !data ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: 14 }}>
                            No data available for this outlet.
                        </div>
                    ) : (
                        <>
                            {/* ── Section 1: Core Stats ── */}
                            <SectionHeader title="Revenue Overview" icon="bi-bar-chart-fill" />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
                                <StatCard icon="bi-currency-rupee" label="Total Sales" value={fmt(stats?.totalSales)} color="#4d8af0" bg="#eef3ff" />
                                <StatCard icon="bi-receipt" label="Net Sales" value={fmt(stats?.netSales)} color="#20c997" bg="#e6faf5" />
                                <StatCard icon="bi-phone" label="App Net Sale" value={fmt(stats?.appNetSale)} color="#6f42c1" bg="#f3eeff" />
                                <StatCard icon="bi-display" label="Kiosk Net Sale" value={fmt(stats?.kioskNetSale)} color="#fd7e14" bg="#fff3e6" />
                            </div>

                            {/* ── Section 2: Order Stats ── */}
                            <SectionHeader title="Order Statistics" icon="bi-bag-check-fill" />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                                <StatCard icon="bi-bag" label="Total Orders" value={fmtNum(stats?.totalOrders)} color="#0ea5e9" bg="#f0f9ff" />
                                <StatCard
                                    icon="bi-x-circle"
                                    label="Cancelled"
                                    value={fmtNum(stats?.cancelledOrders)}
                                    sub={stats?.cancelledOrderAmount ? `${fmt(stats.cancelledOrderAmount)} lost` : null}
                                    color="#ef4444"
                                    bg="#fff5f5"
                                />
                                <StatCard icon="bi-clock-history" label="Delayed Orders" value={fmtNum(stats?.delayedOrders)} color="#f59e0b" bg="#fffbeb" />
                                <StatCard icon="bi-lightning-charge" label="Early Orders" value={fmtNum(stats?.earlyOrders)} color="#22c55e" bg="#f0fdf4" />
                                <StatCard icon="bi-gift" label="Complimentary" value={fmtNum(stats?.complimentaryOrders)} color="#8b5cf6" bg="#f5f3ff" />
                            </div>

                            {/* ── Section 3: Online Sales ── */}
                            <SectionHeader title="Online Platform Sales" icon="bi-globe2" />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
                                <StatCard
                                    icon="bi-bag-heart"
                                    label="Swiggy Revenue"
                                    value={fmt(totalOnlineSales?.swiggy?.revenue)}
                                    sub={`${fmtNum(totalOnlineSales?.swiggy?.totalOrders)} orders`}
                                    color="#ff6900" bg="#fff4ed"
                                />
                                <StatCard
                                    icon="bi-fire"
                                    label="Zomato Revenue"
                                    value={fmt(totalOnlineSales?.zomato?.revenue)}
                                    sub={`${fmtNum(totalOnlineSales?.zomato?.totalOrders)} orders`}
                                    color="#e23744" bg="#fff5f5"
                                />
                                <StatCard
                                    icon="bi-globe"
                                    label="Total Online"
                                    value={fmt(totalOnlineSales?.totalRevenue)}
                                    color="#0ea5e9" bg="#f0f9ff"
                                />
                            </div>

                            {/* ── Section 4: Hourly Distribution ── */}
                            {hourlyDistribution?.length > 0 && (
                                <>
                                    <SectionHeader title="Hourly Distribution" icon="bi-clock-fill" />
                                    <div style={{ background: '#f9fafb', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 10 }}>
                                            <span style={{ fontSize: 11, color: '#9ca3af', width: 120 }}>Time Slot</span>
                                            <span style={{ flex: 1, fontSize: 11, color: '#9ca3af' }}>Volume</span>
                                            <span style={{ width: 55, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>Orders</span>
                                            <span style={{ width: 80, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>Net Sales</span>
                                        </div>
                                        {hourlyDistribution.map((h, i) => (
                                            <HourlyBar
                                                key={i}
                                                slot={h.timeSlot}
                                                orders={h.totalOrders}
                                                sales={h.netSales}
                                                maxOrders={maxOrders}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* ── Section 5: Products ── */}
                            <SectionHeader title="Top & Bottom Products" icon="bi-grid-fill" />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <i className="bi bi-trophy-fill" style={{ fontSize: 12 }} /> Top Performers
                                    </div>
                                    {(topProducts || []).map((p, i) => (
                                        <RankRow key={p._id} rank={i + 1} name={p.productName} qty={p.totalQty} isTop={true} />
                                    ))}
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <i className="bi bi-arrow-down-circle-fill" style={{ fontSize: 12 }} /> Low Performers
                                    </div>
                                    {(bottomProducts || []).map((p, i) => (
                                        <RankRow key={p._id} rank={i + 1} name={p.productName} qty={p.totalQty} isTop={false} />
                                    ))}
                                </div>
                            </div>

                            {/* ── Section 6: Coupons ── */}
                            {topCoupons?.length > 0 && (
                                <>
                                    <SectionHeader title="Top Coupons Used" icon="bi-tag-fill" />
                                    <div style={{ marginBottom: 20 }}>
                                        {topCoupons.map((c) => (
                                            <CouponRow key={c._id} coupon={c.couponCode} used={c.totalUsed} discount={c.totalDiscount} />
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* ── Section 7: Shift + Users + Petty Cash ── */}
                            <SectionHeader title="Operational Summary" icon="bi-clipboard-data-fill" />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
                                <StatCard
                                    icon="bi-door-open"
                                    label="Shift Opened"
                                    value={formatShiftTime(outletShiftSummary?.openTime)}
                                    sub={outletShiftSummary?.closeTime ? `Closed: ${formatShiftTime(outletShiftSummary.closeTime)}` : 'Still open'}
                                    color="#0ea5e9" bg="#f0f9ff"
                                />
                                <StatCard
                                    icon="bi-people-fill"
                                    label="New Users"
                                    value={fmtNum(userStatsSummary?.newUserJoinedCount)}
                                    sub={`${fmtNum(userStatsSummary?.repeatedUserCount)} returning`}
                                    color="#8b5cf6" bg="#f5f3ff"
                                />
                                <StatCard
                                    icon="bi-cash-stack"
                                    label="Petty Cash (Net)"
                                    value={fmt(pettyCashSummary?.netExpense)}
                                    sub={`Credit: ${fmt(pettyCashSummary?.totalCreditAmount)} / Debit: ${fmt(pettyCashSummary?.totalDebitAmount)}`}
                                    color="#f59e0b" bg="#fffbeb"
                                />
                                <StatCard
                                    icon="bi-exclamation-triangle-fill"
                                    label="Exhausted Stock"
                                    value={fmtNum(exhaustedStockSummary?.totalExhaustedEntries)}
                                    sub={`${fmtNum(damagedProductsSummary?.totalDamagedProducts)} damaged products`}
                                    color="#ef4444" bg="#fff5f5"
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Modal Footer */}
                <div style={{
                    padding: '12px 24px', borderTop: '1px solid #e5e7eb',
                    display: 'flex', justifyContent: 'flex-end', gap: 8,
                    flexShrink: 0, background: '#f9fafb',
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px 20px', borderRadius: 8, border: '1px solid #d1d5db',
                            background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            color: '#374151', transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

OutletDetailsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    outletName: PropTypes.string,
    data: PropTypes.object,
    loading: PropTypes.bool,
};

OutletDetailsModal.defaultProps = {
    outletName: '',
    data: null,
    loading: false,
};

export default OutletDetailsModal;